import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { CompositeFileRecorderOptions, FileRecorder, FileRecorderClosePayload, FileRecorderCloseResult } from './types';

const logger = createLogger('recorder:composite');
const DEFAULT_PRIMARY_CLOSE_TIMEOUT_MS = 2000;

export class CompositeFileRecorder implements FileRecorder {
    private readonly primary: FileRecorder;
    private readonly backup: FileRecorder;
    private readonly primaryCloseTimeoutMs: number;

    constructor({
        primary,
        backup,
        primaryCloseTimeoutMs = DEFAULT_PRIMARY_CLOSE_TIMEOUT_MS,
    }: CompositeFileRecorderOptions) {
        this.primary = primary;
        this.backup = backup;
        this.primaryCloseTimeoutMs = primaryCloseTimeoutMs;
    }

    private withCloseTimeout<T>(promise: Promise<T>) {
        return new Promise<T>((resolve, reject) => {
            const timeoutId = window.setTimeout(() => {
                reject(new Error('Primary recorder close timeout'));
            }, this.primaryCloseTimeoutMs);

            promise
                .then((result) => resolve(result))
                .catch((error) => reject(error))
                .finally(() => {
                    clearTimeout(timeoutId);
                });
        });
    }

    async init(): Promise<void> {
        const [primaryResult, backupResult] = await Promise.allSettled([this.primary.init(), this.backup.init()]);

        if (primaryResult.status === 'rejected') {
            logger.error(primaryResult.reason, { method: 'init:primary' });
        }
        if (backupResult.status === 'rejected') {
            logger.error(backupResult.reason, { method: 'init:backup' });
        }

        if (primaryResult.status === 'rejected' && backupResult.status === 'rejected') {
            throw primaryResult.reason;
        }
    }

    async addChunk(chunk: Blob): Promise<void> {
        const [primaryResult, backupResult] = await Promise.allSettled([
            this.primary.addChunk(chunk),
            this.backup.addChunk(chunk),
        ]);

        if (primaryResult.status === 'rejected') {
            logger.error(primaryResult.reason, { method: 'addChunk:primary' });
        }
        if (backupResult.status === 'rejected') {
            logger.error(backupResult.reason, { method: 'addChunk:backup' });
        }

        if (primaryResult.status === 'rejected' && backupResult.status === 'rejected') {
            throw primaryResult.reason;
        }
    }

    async close(options?: FileRecorderClosePayload): Promise<FileRecorderCloseResult> {
        if (options?.withoutSave) {
            await Promise.allSettled([
                this.primary.close({ withoutSave: true }),
                this.backup.close({ withoutSave: true }),
            ]);
            return { status: 'discarded' };
        }

        if (options?.preserveForRecovery) {
            try {
                await this.withCloseTimeout(this.primary.close({ preserveForRecovery: true }));
            } catch (error) {
                logger.error(error, { method: 'close:primary-preserve' });
            }

            try {
                return await this.backup.close({ preserveForRecovery: true });
            } catch (error) {
                logger.error(error, { method: 'close:backup-preserve' });
                return { status: 'discarded' };
            }
        }

        try {
            const primaryResult = await this.withCloseTimeout(this.primary.close(options));

            if (primaryResult.status === 'saved') {
                try {
                    await this.backup.close({ withoutSave: true });
                } catch (error) {
                    logger.error(error, { method: 'close:backup-discard' });
                }
                return primaryResult;
            }

            if (primaryResult.status === 'discarded') {
                await this.backup.close({ withoutSave: true });
                return primaryResult;
            }

            return this.backup.close({ preserveForRecovery: true });
        } catch (error) {
            logger.error(error, { method: 'close:primary' });

            try {
                return await this.backup.close({ preserveForRecovery: true });
            } catch (backupError) {
                logger.error(backupError, { method: 'close:backup' });
                return { status: 'discarded' };
            }
        }
    }
}
